import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import CustomCursor from '../../components/CustomCursor';

const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'team', label: 'Team' },
  { id: 'original-brand', label: 'Original Brand' },
  { id: 'the-problem', label: 'The Problem' },
  { id: 'mood-boards', label: 'Mood Boards' },
  { id: 'concept-iterations', label: 'Iterations' },
  { id: 'branding-timeline', label: 'Branding Timeline' },
  { id: 'finalized-concept', label: 'Finalized Concept' },
  { id: 'new-components', label: 'New Components' },
  { id: 'takeaways-results', label: 'Takeaways & Results' },
];

function SideNav() {
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 120;
      let current = 'overview';
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.offsetTop <= scrollY) {
          current = section.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }
  };

  return (
    <nav className="hidden xl:flex flex-col gap-1 fixed left-8 top-1/2 -translate-y-1/2 z-40 w-[160px]">
      {sections.map((s) => (
        <button
          key={s.id}
          onClick={() => scrollTo(s.id)}
          className={`text-left text-[13px] px-3 py-1.5 rounded-md transition-all cursor-pointer whitespace-nowrap ${
            activeSection === s.id
              ? 'text-notion-accent font-medium bg-notion-accent/10'
              : 'text-notion-secondary hover:text-notion-text hover:bg-notion-hover'
          }`}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

function RoundCard({
  number,
  items,
}: {
  number: string;
  items: string[];
}) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-full bg-notion-accent flex items-center justify-center flex-shrink-0">
          <span className="text-white font-medium text-sm">{number}</span>
        </div>
        <h3 className="font-serif text-xl font-medium text-notion-text">
          Round {number === 'F' ? '— Final' : number}
        </h3>
      </div>
      <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 ml-11">
        <ul className="space-y-3">
          {items.map((item, i) => (
            <li
              key={i}
              className="text-[15px] text-notion-secondary leading-relaxed flex items-start gap-2"
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-fill text-notion-accent"></i>
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function RaremarqBrandIdentityPage() {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start end", "end start"]
  });
  
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.95, 1]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0.8, 1]);
  const y = useTransform(scrollYProgress, [0, 0.5], [30, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans cursor-none">
      <CustomCursor />
      <SideNav />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-notion-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 text-[14px] text-notion-secondary hover:text-notion-text transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </span>
              <span>Back to Portfolio</span>
            </Link>
            <Link
              to="/"
              className="font-serif text-[18px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
            >
              annie
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-6 lg:px-0">
        {/* Hero / Overview */}
        <section id="overview" className="pt-12 lg:pt-16 mb-16">
          <div className="flex items-center gap-2 mb-6">
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              Brand Identity
            </span>
            <span className="text-[12px] text-notion-secondary bg-notion-tag px-2 py-1 rounded">
              2025
            </span>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl font-medium text-notion-text leading-tight tracking-tight mb-6">
            Raremarq Brand Identity
          </h1>
          <p className="text-[17px] text-notion-secondary leading-relaxed mb-4">
            Raremarq is a social marketplace designed to connect collectors, artists, and sellers in a seamless buying and selling experience. It offers a curated space for discovering original art, rare collectibles, and limited-edition pieces while fostering a community-driven ecosystem.
          </p>
          <p className="text-[17px] text-notion-secondary leading-relaxed mb-10">
            Unlike traditional e-commerce platforms, Raremarq integrates social engagement, auctions, and live interactions, making it a unique destination for enthusiasts. We aim to provide one of one experiences.
          </p>

          {/* Hero Image with Animation */}
          <motion.div
            ref={heroRef}
            style={{ scale, opacity, y }}
            className="w-full rounded-lg overflow-hidden bg-white"
          >
            <div className="w-full flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/eee8b2eeb217fc1aa03dc60dd871b1de.png"
                alt="Raremarq Brand Identity Hero"
                title="Raremarq Brand Identity - Annie Ding Portfolio"
                className="w-full h-auto object-contain"
              />
            </div>
          </motion.div>
        </section>

        {/* Project Details */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                My Role
              </h3>
              <p className="text-[15px] text-notion-text font-medium">
                UX Designer
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Timeline
              </h3>
              <p className="text-[15px] text-notion-text font-medium">
                Jan 14 – Apr 7, 2025
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6">
              <h3 className="text-[12px] font-medium text-notion-secondary mb-2 uppercase tracking-wide">
                Tools
              </h3>
              <p className="text-[15px] text-notion-text font-medium">Figma</p>
            </div>
          </div>
        </section>

        {/* Team */}
        <section id="team" className="mb-16">
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
            <h3 className="text-[12px] font-medium text-notion-secondary mb-5 uppercase tracking-wide">
              Team
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { initials: 'CT', name: 'Christopher Tung', role: 'Founder' },
                { initials: 'AD', name: 'Annie Ding', role: 'UX Designer' },
                { initials: 'SB', name: 'Scott Bair', role: 'Studio Designer @ Lunour' },
                { initials: 'MB', name: 'Matt Battaglia', role: 'Graphic Designer' },
                { initials: 'JS', name: 'Jacob Singer', role: 'Co-Founder and CTO' },
              ].map((m) => (
                <div key={m.name} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-notion-accent font-medium text-sm">
                      {m.initials}
                    </span>
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-notion-text">{m.name}</p>
                    <p className="text-[12px] text-notion-secondary">{m.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Collaboration Note */}
          <div className="mt-4 bg-notion-sidebar border border-notion-border rounded-lg p-6">
            <p className="text-[15px] text-notion-secondary leading-relaxed">
              Brand direction developed in collaboration with external studio Lunour. I led product translation, system implementation, and iterative feedback across three rounds.
            </p>
          </div>
        </section>

        {/* Original Brand Identity */}
        <section id="original-brand" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            Nerd Crawler&apos;s Original Brand Identity
          </h2>
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8 mb-8">
            <div className="space-y-4">
              <div>
                <h4 className="text-[14px] font-medium text-notion-text mb-1">Typography</h4>
                <p className="text-[15px] text-notion-secondary">Montserrat</p>
              </div>
              <div>
                <h4 className="text-[14px] font-medium text-notion-text mb-1">Design Approach</h4>
                <p className="text-[15px] text-notion-secondary">Simple, plain layout, no texture</p>
              </div>
            </div>
          </div>
          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/3498b32c5fa6003dfcdef1effe5b0f4a.png"
              alt="Nerd Crawler Original Brand Identity"
              title="Nerd Crawler Original Brand Identity - Annie Ding Portfolio"
              className="w-full h-auto object-contain"
            />
          </div>
        </section>

        {/* The Problem */}
        <section id="the-problem" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            The Problem
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            The original brand lacked visual consistency and didn&apos;t reflect the warmth or premium feel we wanted for the brand. My goal was to lead the rebranding of the platform to better align with our community&apos;s values and support future scalability as our current design was built on whatever we had to get the product live.
          </p>

          {/* User Feedback Quotes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {[
              '\u201CIt looks simple.\u201D',
              '\u201CIt doesn\u2019t really have any color\u201D',
              '\u201CEverything works but, it doesn\u2019t look aesthetically pleasing.\u201D',
              '\u201CThe colors are really bland.\u201D',
            ].map((q, i) => (
              <div
                key={i}
                className="bg-notion-sidebar border-l-4 border-notion-accent p-6 rounded-r-lg"
              >
                <p className="text-[15px] text-notion-text italic">{q}</p>
              </div>
            ))}
          </div>

          <p className="text-[16px] text-notion-secondary leading-relaxed mb-6">
            Users described it as &ldquo;bland&rdquo; and &ldquo;uninspiring,&rdquo; which limited brand trust and failed to convey the &ldquo;premium&rdquo; feeling of one-of-one products we had on the platforms. Raremarq had artists like Frank Cho &amp; Artgerm which are some of the top comic artists in the space. The sparse color use and inconsistent visual hierarchy made very it difficult to distinguish between content types or have a brand identity.
          </p>

          <div className="bg-notion-tag border border-notion-border rounded-lg p-8 mb-8">
            <p className="text-[16px] text-notion-text leading-relaxed mb-4">
              As our team scaled and our valuation approached <strong>$10M</strong>, a common theme emerged from investor conversations: the product looked too early-stage.
            </p>
            <p className="text-[16px] text-notion-text leading-relaxed">
              Every VC we spoke with emphasized the need for a more cohesive, premium visual identity to match the product&apos;s ambition and traction. Without a scalable design system or strong branding foundation, it became increasingly difficult to build new features.
            </p>
          </div>

          <p className="text-[16px] text-notion-secondary leading-relaxed">
            I provided feedback to the external studio shaping the new brand direction. I then lead the full rollout across our platform. My work focused on translating a new visual identity into a flexible, scalable product experience that aligned with both investor expectations and our users.
          </p>
        </section>

        {/* Mood Boards */}
        <section id="mood-boards" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-4">
            Mood Board Exploration
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-10">
            We started by crafting three types of mood boards focusing on community, artistic value, and premium.
          </p>

          {/* Concept 1 */}
          <div className="mb-12">
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mb-6">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/88206c336f1a7012ac2f471c8b265f36.png"
                alt="Mood Board #1 - Approachable styling concept"
                title="Mood Board #1 - Raremarq Brand Identity"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
              <h3 className="font-serif text-xl font-medium text-notion-text mb-4">
                Concept #1 Observations
              </h3>
              <div className="space-y-5">
                <div>
                  <h4 className="text-[14px] font-medium text-notion-accent mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-add-circle-line"></i>
                    </span>
                    Strengths
                  </h4>
                  <ul className="space-y-2 ml-7">
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      The simple, bold, and casual tone allows for a more conversational design language that can develop over time.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      A clean &ldquo;neubrutalist&rdquo; style can be really eyecatching and memorable. Just need to be careful to not over do it.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Works nicely when paired with a few key unique textures to make it our own. And breaks up the solid walls of color.
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-[14px] font-medium text-red-500 mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-indeterminate-circle-line"></i>
                    </span>
                    Concerns
                  </h4>
                  <ul className="space-y-2 ml-7">
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Overly expressive illustration and characters. These elements can start to lock the brand in to a top down identity.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      It can be super subjective on taste as the brand expands audiences over time.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Can get very expensive and time consuming to maintain.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Concept 2 */}
          <div className="mb-12">
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mb-6">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/bac51ebfb55cd282a477e969bde55a70.png"
                alt="Mood Board #2 - Handcrafted and real concept"
                title="Mood Board #2 - Raremarq Brand Identity"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
              <h3 className="font-serif text-xl font-medium text-notion-text mb-4">
                Concept #2 Observations
              </h3>
              <div className="space-y-5">
                <div>
                  <h4 className="text-[14px] font-medium text-notion-accent mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-add-circle-line"></i>
                    </span>
                    Strengths
                  </h4>
                  <ul className="space-y-2 ml-7">
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      The hand written scribble style is very human and authentic, communicating warmth and personal touch.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Creates a sense of intimacy and approachability that could resonate with creative communities.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      The organic, imperfect aesthetic feels genuine and could differentiate from more polished competitors.
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-[14px] font-medium text-red-500 mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-indeterminate-circle-line"></i>
                    </span>
                    Concerns
                  </h4>
                  <ul className="space-y-2 ml-7">
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      The hand written scribble style while very human, also communicates a roughness of seeing Work in Progress and the collaborative process. But not the well crafted and considered final work.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      This is why we see this style with a lot of productivity SaaS platforms. Maybe not an evocation we want people to have.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Feels a little too &ldquo;scrapbooky&rdquo; feel for Nerd Crawler, we want a balance between more artsy but, modern multimedia.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Concept 3 */}
          <div className="mb-4">
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mb-6">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/cd8544b477e34e1d845ef7ea3219ed65.png"
                alt="Mood Board #3 - High-end and rare concept"
                title="Mood Board #3 - Raremarq Brand Identity"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
              <h3 className="font-serif text-xl font-medium text-notion-text mb-4">
                Concept #3 Observations
              </h3>
              <div className="space-y-5">
                <div>
                  <h4 className="text-[14px] font-medium text-notion-accent mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-add-circle-line"></i>
                    </span>
                    Strengths
                  </h4>
                  <ul className="space-y-2 ml-7">
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Much more crafted, elevated, and considered of a look and feel. High risk but high reward. Feels "premium"
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      This approach could feel much more opinionated about what good design is. You would feel Raremarq itself, as a brand, has an expectation on the level of quality they are curating.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Creates a premium, gallery-like atmosphere that elevates the art and collectibles being showcased.
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-[14px] font-medium text-red-500 mb-2 flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-indeterminate-circle-line"></i>
                    </span>
                    Concerns
                  </h4>
                  <ul className="space-y-2 ml-7">
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Where concept 1&apos;s more minimal brand vibe allows the dialogue between creator and fan to be the focus, this approach might overshadow user content.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Could feel intimidating or exclusive to casual users or emerging artists who don&apos;t yet have &ldquo;gallery-worthy&rdquo; work.
                    </li>
                    <li className="text-[15px] text-notion-secondary leading-relaxed">
                      Requires consistent high-quality execution across all touchpoints, which can be resource-intensive to maintain.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Concept Iterations */}
        <section id="concept-iterations" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-4">
            Concept Iterations
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            Early explorations focused on translating the new brand direction into scalable interface components and layout systems.
          </p>
          <div className="flex flex-col gap-6">
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/9becff67c89a92838df87641391f7f3d.png"
                alt="Concept Iteration 1"
                title="Concept Iteration 1 - Raremarq Brand Identity"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/77695ea38b27d6f68c8cebc491561136.png"
                alt="Concept Iteration 2"
                title="Concept Iteration 2 - Raremarq Brand Identity"
                className="w-full h-auto object-contain"
              />
            </div>
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/c4ca181b3ca1fa031190d959733bccf6.png"
                alt="Concept Iteration 3"
                title="Concept Iteration 3 - Raremarq Brand Identity"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </section>

        {/* Branding Direction & Timeline */}
        <section id="branding-timeline" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-8">
            Branding Direction &amp; Timeline
          </h2>

          <RoundCard
            number="1"
            items={[
              'Called for a stronger focus on the \u201CR\u201D in the logo, with the spark as a secondary visual.',
              'Suggested rounding out typography (especially the \u201Cr\u201D and \u201Cq\u201D) for a softer, more inviting tone.',
              'Critiqued early color palettes as too harsh or technical \u2014 advised against yellow/black and overly saturated combinations.',
              'Highlighted the need for a brand that felt warm, premium, and scalable \u2014 not overly abstract or techy.',
            ]}
          />

          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mb-10">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/7fc05b2a7d62cc44989f70f57e2079b0.png"
              alt="Round 1 Design Iterations"
              title="Round 1 Design Iterations - Raremarq Brand Identity"
              className="w-full h-auto object-contain"
            />
          </div>

          <RoundCard
            number="2"
            items={[
              'Supported ink-style, liquefied typography but pushed for thinner weights and more legible flow.',
              'Preferred notched \u201CR\u201D designs over fully circular or abstract forms.',
              'Rejected logos that resembled other shapes (e.g., arrow, spade, or \u201CK\u201D).',
              'Encouraged testing more muted, pastel colorways to let art remain the focal point.',
              'Suggested improving letter spacing and exploring a capital \u201CQ\u201D for clearer readability.',
            ]}
          />

          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mb-10">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/3b5c9b6ed78e715abcc18d614f7589f1.png"
              alt="Round 2 Design Iterations"
              title="Round 2 Design Iterations - Raremarq Brand Identity"
              className="w-full h-auto object-contain"
            />
          </div>

          <RoundCard
            number="3"
            items={[
              'Selected a refined C3 palette with a request to desaturate the background yellow slightly.',
              'Highlighted dark green and bright orange as strong, community-oriented accents.',
              'Finalized logo and typography choices.',
              'Raised final question around the balance between visual branding (\u201CQ\u201D) and how the name is typed/spelled (\u201CRaremarq\u201D).',
            ]}
          />

          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mb-10">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/d705e5057c5295d1f04bbba095fca5a3.jpeg"
              alt="Round 3 Final Design Iterations"
              title="Round 3 Final Design Iterations - Raremarq Brand Identity"
              className="w-full h-auto object-contain"
            />
          </div>

          {/* Feedback Document Links */}
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 mb-4">
            <h4 className="text-[13px] font-medium text-notion-secondary uppercase tracking-wide mb-4">
              Feedback Documents
            </h4>
            <div className="flex flex-col gap-3">
              {[
                { label: 'Round 1 Feedback Document', href: 'https://docs.google.com/document/d/1IKvWD_ReHf2oUOYL0J0-nagI_x49kCTqTzT4XmgD0fY/edit?usp=sharing' },
                { label: 'Round 2 Feedback Document', href: 'https://docs.google.com/document/d/1grJejXyArFeiZoN5FBmw9sDyuKdmwjTMDh1YQw1OPKI/edit?usp=sharing' },
                { label: 'Round 3 Feedback Document', href: 'https://docs.google.com/document/d/19A5SoTnZcOZwvoliPoiR6HuV3JuacIH440gQDMvfI80/edit?usp=sharing' },
              ].map((doc) => (
                <a
                  key={doc.label}
                  href={doc.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="flex items-center gap-2 text-[14px] text-notion-accent hover:underline cursor-pointer group"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-file-text-line text-notion-accent group-hover:text-notion-accent/80"></i>
                  </span>
                  {doc.label}
                  <span className="w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-external-link-line text-notion-secondary text-[12px]"></i>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Finalized Concept */}
        <section id="finalized-concept" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-4">
            Finalized Concept
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            The final direction balances warmth, clarity, and scalability — built to grow with the brand and resonate with both users and investors.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/93d0b16535b2eb0850546b8b2e1b2364.png"
                alt="Raremarq Final Logo"
                title="Raremarq Final Logo - Brand Identity"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/ea5e679fed6d58068b04ceaca18a3c80.png"
                alt="Raremarq Final Color Palette"
                title="Raremarq Final Color Palette - Brand Identity"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mb-8">
            <img
              src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/8612b1ca0e40de8c0bdc4411827834cb.jpeg"
              alt="Raremarq Typography Hierarchy"
              title="Raremarq Typography Hierarchy - Brand Identity"
              className="w-full h-auto object-contain"
            />
          </div>
        </section>

        {/* New Components */}
        <section id="new-components" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-4">
            New Components
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            Built a scalable component library to ensure consistency across pages and accelerate future development.
          </p>
          
          {/* Component Set 1 - Buttons/Labels/Tags */}
          <div className="mb-6">
            <h3 className="text-[14px] font-medium text-notion-text mb-2">Buttons, Labels &amp; Tags</h3>
            <p className="text-[14px] text-notion-secondary mb-4">
              A comprehensive set of button states, icon buttons, and status labels designed for consistency across the platform.
            </p>
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/decde40ed5d97f450b97f3390eadc85f.png"
                alt="Component Set 1 - Buttons, Labels and Tags"
                title="Raremarq Component Set 1 - Buttons, Labels and Tags"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* Component Set 2 - Icon Library */}
          <div className="mb-6">
            <h3 className="text-[14px] font-medium text-notion-text mb-2">Icon Library</h3>
            <p className="text-[14px] text-notion-secondary mb-4">
              A custom icon library built to support the new brand identity with consistent visual language and scalable design.
            </p>
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/aeda1c1c2a2a306e90b3702e01f0ef74.png"
                alt="Component Set 2 - Icon Library"
                title="Raremarq Component Set 2 - Icon Library"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Component Set 3 - Shadow System */}
            <div className="flex flex-col">
              <h3 className="text-[14px] font-medium text-notion-text mb-2">Shadow System</h3>
              <p className="text-[14px] text-notion-secondary mb-4 min-h-[40px]">
                A comprehensive shadow system designed for variety of modules and cards to create depth and visual hierarchy.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mt-auto">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/68c69e2f1a8c7f7fe6db62b9b711dd03.png"
                  alt="Component Set 3 - Shadow System"
                  title="Raremarq Component Set 3 - Shadow System"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>

            {/* Component Set 4 - Mobile Bottom Nav */}
            <div className="flex flex-col">
              <h3 className="text-[14px] font-medium text-notion-text mb-2">Mobile Bottom Navigation</h3>
              <p className="text-[14px] text-notion-secondary mb-4 min-h-[40px]">
                A mobile-optimized bottom navigation system with clear iconography and notification badges for seamless mobile experience.
              </p>
              <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center mt-auto">
                <img
                  src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/2ad710e51457ecde4d05ff365697f518.png"
                  alt="Component Set 4 - Mobile Bottom Navigation"
                  title="Raremarq Component Set 4 - Mobile Bottom Navigation"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Component Set 5 - Top Navigation Bar for Desktop */}
          <div className="mb-6">
            <h3 className="text-[14px] font-medium text-notion-text mb-2">Top Navigation Bar for Desktop</h3>
            <p className="text-[14px] text-notion-secondary mb-4">
              The primary desktop navigation system featuring search, category browsing, user menus, and contextual dropdowns — designed for clarity and efficient wayfinding.
            </p>
            <div className="w-full rounded-lg overflow-hidden border border-notion-border bg-white flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/b5b1f00af36262473645e32292f04b0c.png"
                alt="Component Set 5 - Top Navigation Bar for Desktop"
                title="Raremarq Component Set 5 - Top Navigation Bar for Desktop"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </section>

        {/* Takeaways & Results */}
        <section id="takeaways-results" className="mb-16">
          <h2 className="font-serif text-3xl font-medium text-notion-text mb-6">
            Takeaways &amp; Results
          </h2>
          <p className="text-[16px] text-notion-secondary leading-relaxed mb-8">
            The rebrand transformed how users perceived and engaged with Raremarq, directly contributing to significant growth in platform adoption and user trust.
          </p>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 text-center">
              <div className="text-3xl font-serif font-medium text-notion-accent mb-2">
                1,000%
              </div>
              <p className="text-[14px] text-notion-secondary">
                Spike in Traffic
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 text-center">
              <div className="text-3xl font-serif font-medium text-notion-accent mb-2">
                301%
              </div>
              <p className="text-[14px] text-notion-secondary">
                YoY Increase in Active Users
              </p>
            </div>
            <div className="bg-notion-sidebar border border-notion-border rounded-lg p-6 text-center">
              <div className="text-3xl font-serif font-medium text-notion-accent mb-2">
                45K
              </div>
              <p className="text-[14px] text-notion-secondary">
                Total Users
              </p>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-8">
            <h3 className="text-[14px] font-medium text-notion-secondary uppercase tracking-wide mb-5">
              Key Takeaways
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-lightbulb-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    Brand perception directly impacts growth
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    A cohesive, premium visual identity helped convert investor skepticism into confidence and drove measurable user acquisition.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-team-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    Collaborative iteration yields stronger outcomes
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    Working closely with an external studio while providing structured feedback ensured the final direction balanced creativity with strategic goals.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-palette-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    Warmth and premium can coexist
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    The final palette and typography struck a balance between approachable community vibes and elevated, gallery-worthy aesthetics.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-notion-accent/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-stack-line text-notion-accent text-sm"></i>
                </div>
                <div>
                  <h4 className="text-[15px] font-medium text-notion-text mb-1">
                    Scalable systems accelerate future work
                  </h4>
                  <p className="text-[14px] text-notion-secondary leading-relaxed">
                    Building a component library from the start enabled faster feature development and maintained consistency as the product grew.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-20">
          <div className="bg-notion-sidebar border border-notion-border rounded-lg p-10">
            <div className="flex flex-col items-center text-center gap-6">
              <div>
                <h3 className="font-serif text-2xl font-medium text-notion-text mb-2">
                  Let&apos;s see what I can build with these components!
                </h3>
                <p className="text-[15px] text-notion-secondary">
                  Full Web Rebrand &mdash; Part 2
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                <a
                  href="https://raremarq.com/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-notion-border rounded-lg text-[14px] font-medium text-notion-text hover:bg-notion-hover transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                  Check out Raremarq Live
                </a>
                <Link
                  to="/raremarq-web-platform"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-notion-accent text-white rounded-lg text-[14px] font-medium hover:bg-notion-accent/90 transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
                >
                  Go to Part 2
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-notion-border bg-notion-sidebar">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <Link
                to="/"
                className="font-serif text-[20px] font-medium text-notion-text hover:text-notion-accent transition-colors cursor-pointer"
              >
                annie
              </Link>
              <p className="text-[13px] text-notion-secondary mt-2">
                Product Designer &amp; UX Strategist
              </p>
            </div>
            <div className="flex items-center gap-6">
              <a
                href="mailto:ading@ucdavis.edu"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                ading@ucdavis.edu
              </a>
              <a
                href="https://www.linkedin.com/in/annieding04/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[13px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
